import { orderBy } from 'lodash';
import { ElementRef, Injectable } from '@angular/core';
import { Selector, State, Store } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatStates from '../../internal';
import { MediaControlService } from '@medsurf/flat-services';

export interface ImageViewerStateModel {
  defaultConfig: any;
  elementRef: ElementRef | null;
}

@State<ImageViewerStateModel>({
  name: 'imageViewer',
  defaults: {
    defaultConfig: {
      showNavigationControl: false,
      navigatorPosition: 'ABSOLUTE',
      navigatorHeight: 105,
      navigatorWidth: 155,
      navigatorTop: 16,
      navigatorLeft: 'calc(100% - 221px)',
      navigatorAutoResize: false,
      autoHideControls: false,
      gestureSettingsMouse: {
        flickEnabled: false,
        clickToZoom: false,
        dblClickToZoom: true,
        scrollToZoom: true,
      },
      gestureSettingsPen: {
        flickEnabled: false
      },
      gestureSettingsTouch: {
        flickEnabled: false
      },
      gestureSettingsUnknown: {
        flickEnabled: false
      },
      sequenceMode: false,
      imageLoaderLimit: 10,
      showSequenceControl: false,
      preserveViewport: true,
      maxZoomPixelRatio: 2,
      animationTime: 0.7,
      panVertical: true,
      panHorizontal: true,
      showNavigator: true,
    },
    elementRef: null,
  }
})
@Injectable()
export class ImageSharedState {
  constructor(public store: Store,
              public mediaControlService: MediaControlService) {
  }

  //<editor-fold desc="Selectors">

  @Selector([ImageSharedState])
  static defaultConfig$(state: ImageViewerStateModel) {
    return state.defaultConfig;
  }

  @Selector([ImageSharedState])
  static elementRef$(state: ImageViewerStateModel) {
    return state.elementRef;
  }

  @Selector([ImageSharedState.elementRef$])
  static elementConfig$(elementRef: ElementRef) {
    return elementRef !== null ? {element: elementRef.nativeElement} : null;
  }

  @Selector([FlatStates.ImageEntityState.currentSelectedSlideImage$])
  static imageConfig$(currentSelectedSlideImage: FlatModels.ImageEntityModels.Image) {
    // Navigator Background Color
    const navigatorBackgroundColor = currentSelectedSlideImage && currentSelectedSlideImage.background && currentSelectedSlideImage.background.color || '';

    // Return Image Config
    return {
      navigatorBackground: navigatorBackgroundColor.startsWith('#') ? navigatorBackgroundColor : '#ffffff',
    };
  }

  @Selector([ImageSharedState.defaultConfig$])
  static config$(defaultConfig: any) {
    if (defaultConfig !== null) {
      return structuredClone(defaultConfig) || null;
    }
    return null;
  }

  @Selector([
    FlatStates.AnnotationEntityState.currentSelectedSlideImageAnnotations$,
    FlatStates.AnnotationLabelEntityState.typedEntities$,
    FlatStates.AnnotationSourceEntityState.typedEntities$,
    FlatStates.AnnotationTargetEntityState.typedEntities$,
    FlatStates.FreeFormStyleEntityState.typedEntities$,
    FlatStates.KeymapStyleEntityState.typedEntities$,
    FlatStates.KeymapColumnEntityState.typedEntities$,
    FlatStates.KeymapLabelEntityState.typedEntities$,
  ])
  static annotations$(currentSelectedSlideImageAnnotations: FlatModels.AnnotationEntityModels.AnnotationEntityType[],
                      typedAnnotationLabels: { [id: string]: FlatModels.AnnotationLabelEntityModels.AnnotationLabel },
                      typedAnnotationSources: { [id: string]: FlatModels.AnnotationSourceEntityModels.AnnotationSource },
                      typedAnnotationTargets: { [id: string]: FlatModels.AnnotationTargetEntityModels.AnnotationTarget },
                      typedFreeformStyles: { [id: string]: FlatModels.FreeFormStyleEntityModels.FreeFormStyle },
                      typedKeymapStyles: { [id: string]: FlatModels.KeymapStyleEntityModels.KeymapStyle },
                      typedKeymapColumns: { [id: string]: FlatModels.KeymapColumnEntityModels.KeymapColumn },
                      typedKeymapLabels: { [id: string]: FlatModels.KeymapLabelEntityModels.KeymapLabel }): FlatModels.ImageSharedModels.ElementModels[] {
    if (Array.isArray(currentSelectedSlideImageAnnotations)) {
      return currentSelectedSlideImageAnnotations.reduce((arr, annotation) => {
        let marker: FlatModels.AnnotationEntityModels.Marker;
        let freeForm: FlatModels.AnnotationEntityModels.FreeForm;
        let keymap: FlatModels.AnnotationEntityModels.Keymap;
        switch (annotation.type) {
          case FlatModels.AnnotationEntityModels.AnnotationType.enum.Marker:
            marker = annotation as FlatModels.AnnotationEntityModels.Marker;
            arr.push({
              id: marker.id,
              annotation: structuredClone(marker),
              label: structuredClone(typedAnnotationLabels[marker.label as unknown as string]),
              source: structuredClone(typedAnnotationSources[marker.source as unknown as string]),
              targets: (marker.targets as unknown as string[] || []).reduce((tarr: FlatModels.AnnotationTargetEntityModels.AnnotationTarget[], tid) => {
                const tentity = structuredClone(typedAnnotationTargets[tid]);
                if (tentity) {
                  tarr.push(tentity);
                }
                return tarr;
              }, []),
            });
            break;
          case FlatModels.AnnotationEntityModels.AnnotationType.enum.FreeForm:
            freeForm = annotation as FlatModels.AnnotationEntityModels.FreeForm;
            arr.push({
              id: freeForm.id,
              annotation: structuredClone(freeForm),
              label: structuredClone(typedAnnotationLabels[freeForm.label as unknown as string]),
              source: structuredClone(typedAnnotationSources[freeForm.source as unknown as string]),
              target: structuredClone(typedAnnotationTargets[freeForm.target as unknown as string]),
              freeFormStyle: structuredClone(typedFreeformStyles[freeForm.freeFormStyle as unknown as string]),
            });
            break;
          case FlatModels.AnnotationEntityModels.AnnotationType.enum.Keymap:
            keymap = annotation as FlatModels.AnnotationEntityModels.Keymap;
            arr.push({
              id: keymap.id,
              annotation: structuredClone(keymap),
              label: structuredClone(typedAnnotationLabels[keymap.label as unknown as string]),
              source: structuredClone(typedAnnotationSources[keymap.source as unknown as string]),
              // targets: annotationTargets.filter(at => (keymap.targets as unknown as string[]).includes(at.id)) || [],
              keymapStyle: structuredClone(typedKeymapStyles[keymap.keymapStyle as unknown as string]),
              columns: orderBy(
                (keymap.columns as unknown as string[]).reduce((carr: FlatModels.ImageSharedModels.KeymapColumnModel[], cid) => {
                  const centity = structuredClone(typedKeymapColumns[cid]);
                  if (centity) {
                    carr.push({
                      id: centity.id,
                      keymap: centity.keymap,
                      labels: orderBy(
                        (centity.labels as unknown as string[]).reduce((larr: FlatModels.KeymapLabelEntityModels.KeymapLabel[], lid) => {
                          const lentity = structuredClone(typedKeymapLabels[lid]);
                          if (lentity) {
                            larr.push(lentity);
                          }
                          return larr;
                        }, []),
                        (a) => a.order || 0,
                        'asc'
                      ),
                      order: centity.order
                    });
                  }
                  return carr;
                }, []),
                (a) => a.order || 0,
                'asc'
              )
            });
            break;
          /*
          case FlatModels.AnnotationEntityModels.AnnotationType.TEXT:
            return {};
          */
          default:
            throw new FlatModels.ErrorControlModels.MedsurfError(
              'notification.type_not_supported',
              FlatModels.ErrorControlModels.ErrorLevel.enum.error,
              FlatModels.ErrorControlModels.ErrorInstance.enum.AUTHOR,
              "LIBS.FlatStates.ImageSharedState.annotations$"
            );
        }
        return arr;
      }, [] as FlatModels.ImageSharedModels.ElementModels[]);
    }
    return [];
  }

  //</editor-fold>
}
